'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { ArrowLeft, Plus, Trash2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import {
  FARMS_LIST, DEPARTMENTS, PROCUREMENT_CATEGORIES, UNITS, SUPPLIERS,
  PROCUREMENT_METHOD_LABELS, ProcurementMethod, Priority, formatUGX,
} from '../../../components/procurementData';
import { useRole } from '@/lib/useRole';

interface LineItem {
  id: string;
  description: string;
  category: string;
  unit: string;
  quantity: number;
  estimatedUnitCost: number;
  estimatedTotal: number;
  preferredSupplier: string;
  specifications: string;
}

function newLine(): LineItem {
  return { id: Date.now().toString(), description: '', category: 'Feed', unit: 'bag', quantity: 1, estimatedUnitCost: 0, estimatedTotal: 0, preferredSupplier: '', specifications: '' };
}

export default function NewRequisitionContent() {
  const { can } = useRole();
  const canCreate = can('manage_procurement');

  const [form, setForm] = useState({
    farm: 'Idudi Poultry Farm',
    department: '',
    requestedBy: 'Okello James',
    requiredByDate: '',
    priority: 'normal' as Priority,
    procurementMethod: 'rfq' as ProcurementMethod,
    purpose: '',
    budgetCode: '',
    costCentre: '',
    notes: '',
  });
  const [items, setItems] = useState<LineItem[]>([newLine()]);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const total = items.reduce((s, i) => s + i.estimatedTotal, 0);

  function updateItem(id: string, field: keyof LineItem, value: string | number) {
    setItems(prev => prev.map(item => {
      if (item.id !== id) return item;
      const updated = { ...item, [field]: value };
      if (field === 'quantity' || field === 'estimatedUnitCost') {
        updated.estimatedTotal = Number(updated.quantity) * Number(updated.estimatedUnitCost);
      }
      return updated;
    }));
  }

  function validate() {
    const e: Record<string, string> = {};
    if (!form.department) e.department = 'Department is required';
    if (!form.requiredByDate) e.requiredByDate = 'Required by date is required';
    if (!form.purpose.trim()) e.purpose = 'Purpose / justification is required';
    if (form.priority === 'emergency' && form.purpose.length < 30) e.purpose = 'Emergency requests require a detailed justification (min 30 characters)';
    if (!form.budgetCode.trim()) e.budgetCode = 'Budget code is required';
    items.forEach((item, idx) => {
      if (!item.description.trim()) e[`item_${idx}_desc`] = 'Item description required';
      if (item.quantity <= 0) e[`item_${idx}_qty`] = 'Quantity must be > 0';
      if (item.estimatedUnitCost < 0) e[`item_${idx}_cost`] = 'Cost cannot be negative';
    });
    return e;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSubmitted(true);
  }

  if (!canCreate) {
    return (
      <div className="p-8 text-center">
        <AlertTriangle size={40} className="mx-auto text-amber-400 mb-3" />
        <h2 className="text-lg font-bold text-gray-700">Access Denied</h2>
        <p className="text-sm text-gray-500 mt-1">You do not have permission to create purchase requisitions.</p>
        <Link href="/procurement/requisitions" className="mt-4 inline-flex items-center gap-2 text-sm text-orange-600 font-semibold hover:underline">
          <ArrowLeft size={14} /> Back to Requisitions
        </Link>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="p-8 text-center max-w-md mx-auto">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 size={32} className="text-green-600" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Requisition Submitted</h2>
        <p className="text-sm text-gray-500 mb-1">Your purchase requisition has been submitted for approval.</p>
        <p className="text-xs text-gray-400 mb-6">Estimated Total: <strong className="text-gray-700">{formatUGX(total)}</strong></p>
        <div className="flex gap-3 justify-center">
          <Link href="/procurement/requisitions" className="px-4 py-2 bg-orange-600 text-white text-sm font-semibold rounded-xl hover:bg-orange-700 transition-colors">
            View Requisitions
          </Link>
          <button onClick={() => { setSubmitted(false); setItems([newLine()]); setForm(f => ({ ...f, purpose: '', budgetCode: '', notes: '' })); }} className="px-4 py-2 border border-gray-200 text-sm font-semibold rounded-xl hover:bg-gray-50 transition-colors text-gray-700">
            New Requisition
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-6 xl:px-8 py-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Link href="/procurement/requisitions" className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
          <ArrowLeft size={16} className="text-gray-500" />
        </Link>
        <div>
          <h1 className="text-xl font-bold text-gray-900">New Purchase Requisition</h1>
          <p className="text-xs text-gray-400 mt-0.5">Submit an internal request to procure goods or services</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Section 1: Request Details */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h2 className="text-sm font-bold text-gray-800 mb-4">Request Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Farm *</label>
              <select value={form.farm} onChange={e => setForm(f => ({ ...f, farm: e.target.value }))} className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white">
                {FARMS_LIST.map(f => <option key={f}>{f}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Department *</label>
              <select value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value }))} className={`w-full px-3 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white ${errors.department ? 'border-red-400' : 'border-gray-200'}`}>
                <option value="">Select department...</option>
                {DEPARTMENTS.map(d => <option key={d}>{d}</option>)}
              </select>
              {errors.department && <p className="text-xs text-red-500 mt-1">{errors.department}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Requested By *</label>
              <input type="text" value={form.requestedBy} onChange={e => setForm(f => ({ ...f, requestedBy: e.target.value }))} className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Required By Date *</label>
              <input type="date" value={form.requiredByDate} onChange={e => setForm(f => ({ ...f, requiredByDate: e.target.value }))} className={`w-full px-3 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 ${errors.requiredByDate ? 'border-red-400' : 'border-gray-200'}`} />
              {errors.requiredByDate && <p className="text-xs text-red-500 mt-1">{errors.requiredByDate}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Priority *</label>
              <div className="flex flex-wrap gap-2">
                {(['low', 'normal', 'high', 'urgent', 'emergency'] as Priority[]).map(p => (
                  <button key={p} type="button" onClick={() => setForm(f => ({ ...f, priority: p }))}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${form.priority === p ? 'bg-orange-600 text-white border-orange-600' : 'border-gray-200 text-gray-600 hover:border-orange-300'}`}>
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Procurement Method *</label>
              <select value={form.procurementMethod} onChange={e => setForm(f => ({ ...f, procurementMethod: e.target.value as ProcurementMethod }))} className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white">
                {Object.entries(PROCUREMENT_METHOD_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Purpose / Justification *</label>
              <textarea value={form.purpose} onChange={e => setForm(f => ({ ...f, purpose: e.target.value }))} rows={3} placeholder={form.priority === 'emergency' ? 'Emergency justification required — describe the urgency in detail...' : 'Describe the purpose and business need for this procurement...'} className={`w-full px-3 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 resize-none ${errors.purpose ? 'border-red-400' : 'border-gray-200'}`} />
              {errors.purpose && <p className="text-xs text-red-500 mt-1">{errors.purpose}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Budget Code *</label>
              <input type="text" value={form.budgetCode} onChange={e => setForm(f => ({ ...f, budgetCode: e.target.value }))} placeholder="e.g. BC-FEED-2026" className={`w-full px-3 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 ${errors.budgetCode ? 'border-red-400' : 'border-gray-200'}`} />
              {errors.budgetCode && <p className="text-xs text-red-500 mt-1">{errors.budgetCode}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Cost Centre</label>
              <input type="text" value={form.costCentre} onChange={e => setForm(f => ({ ...f, costCentre: e.target.value }))} placeholder="e.g. CC-FARM-001" className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300" />
            </div>
          </div>
        </div>

        {/* Section 2: Line Items */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold text-gray-800">Requisition Items</h2>
            <button type="button" onClick={() => setItems(prev => [...prev, newLine()])} className="flex items-center gap-1.5 text-xs font-semibold text-orange-600 hover:text-orange-700 transition-colors">
              <Plus size={13} /> Add Item
            </button>
          </div>

          <div className="space-y-4">
            {items.map((item, idx) => (
              <div key={item.id} className="border border-gray-100 rounded-xl p-4 bg-gray-50/30">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold text-gray-500">Item {idx + 1}</span>
                  {items.length > 1 && (
                    <button type="button" onClick={() => setItems(prev => prev.filter(i => i.id !== item.id))} className="p-1 rounded hover:bg-red-50 transition-colors">
                      <Trash2 size={13} className="text-red-400" />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  <div className="sm:col-span-2 lg:col-span-2">
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Description *</label>
                    <input type="text" value={item.description} onChange={e => updateItem(item.id, 'description', e.target.value)} placeholder="Item description..." className={`w-full px-3 py-2 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 ${errors[`item_${idx}_desc`] ? 'border-red-400' : 'border-gray-200'} bg-white`} />
                    {errors[`item_${idx}_desc`] && <p className="text-xs text-red-500 mt-1">{errors[`item_${idx}_desc`]}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Category</label>
                    <select value={item.category} onChange={e => updateItem(item.id, 'category', e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white">
                      {PROCUREMENT_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Unit</label>
                    <select value={item.unit} onChange={e => updateItem(item.id, 'unit', e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white">
                      {UNITS.map(u => <option key={u}>{u}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Quantity *</label>
                    <input type="number" min="1" value={item.quantity} onChange={e => updateItem(item.id, 'quantity', Number(e.target.value))} className={`w-full px-3 py-2 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white ${errors[`item_${idx}_qty`] ? 'border-red-400' : 'border-gray-200'}`} />
                    {errors[`item_${idx}_qty`] && <p className="text-xs text-red-500 mt-1">{errors[`item_${idx}_qty`]}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Est. Unit Cost (UGX)</label>
                    <input type="number" min="0" value={item.estimatedUnitCost} onChange={e => updateItem(item.id, 'estimatedUnitCost', Number(e.target.value))} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Preferred Supplier</label>
                    <select value={item.preferredSupplier} onChange={e => updateItem(item.id, 'preferredSupplier', e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white">
                      <option value="">Any supplier</option>
                      {SUPPLIERS.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2 lg:col-span-2">
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Specifications</label>
                    <input type="text" value={item.specifications} onChange={e => updateItem(item.id, 'specifications', e.target.value)} placeholder="Technical specs, brand, quality requirements..." className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white" />
                  </div>
                  <div className="flex items-end">
                    <div className="w-full bg-orange-50 border border-orange-100 rounded-xl px-3 py-2 text-right">
                      <div className="text-xs text-orange-600 font-medium">Line Total</div>
                      <div className="text-sm font-bold text-orange-700">{formatUGX(item.estimatedTotal)}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Total */}
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
            <span className="text-sm font-bold text-gray-700">Requisition Total</span>
            <span className="text-lg font-bold text-gray-900">{formatUGX(total)}</span>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h2 className="text-sm font-bold text-gray-800 mb-3">Additional Notes</h2>
          <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={3} placeholder="Any additional information for the approver or procurement team..." className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 resize-none" />
        </div>

        {/* Submit */}
        <div className="flex items-center justify-between gap-3 pb-6">
          <Link href="/procurement/requisitions" className="px-4 py-2.5 border border-gray-200 text-sm font-semibold rounded-xl hover:bg-gray-50 transition-colors text-gray-700">
            Cancel
          </Link>
          <div className="flex gap-2">
            <button type="button" className="px-4 py-2.5 border border-gray-200 text-sm font-semibold rounded-xl hover:bg-gray-50 transition-colors text-gray-700">
              Save as Draft
            </button>
            <button type="submit" className="px-6 py-2.5 bg-orange-600 text-white text-sm font-semibold rounded-xl hover:bg-orange-700 transition-colors shadow-sm">
              Submit for Approval
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
